import { Box, Divider, Stack } from "@mui/material";
import { type UseModalState } from "@src/lib/ui-react";

import { useSession } from "../Auth/api/useSession";
import { useIsAdmin } from "../ExperimentalHomeHealth/lib/useIsAdmin";
import { Button } from "../redesign/components/Button";
import { DialogFooter } from "../redesign/components/DialogFooter";
import { ResponsiveDialog } from "../redesign/components/ResponsiveDialog";
import { Text } from "../redesign/components/Text";
import { WorkerInvitationStaffMemberRow } from "./components/WorkerInvitationStaffMemberRow";
import { type WorkerInvitationOpenedFrom } from "./types";
import { useWorkerInvitationForm } from "./useWorkerInvitationForm";
import { sendWorkerInvitationDialogActionedEvent } from "./utils";

interface Props {
  openedFrom: WorkerInvitationOpenedFrom;
  modalState: UseModalState;
  isPrnWorkersInvitationRolloutEnabled?: boolean;
  onInvitationSuccess?: () => void;
}

export function WorkerInvitationDialog({
  openedFrom,
  modalState,
  isPrnWorkersInvitationRolloutEnabled = false,
  onInvitationSuccess,
}: Props) {
  const {
    profile: { userId: workplaceId },
  } = useSession();
  const isAdmin = useIsAdmin();

  const {
    control,
    fields,
    dialogContentRef,
    shouldShowFieldError,
    hasAtLeastOneValidRow,
    addNewStaffMember,
    resetForm,
    handleSubmit,
    isSendingInvitations,
  } = useWorkerInvitationForm({
    openedFrom,
    isAdmin,
    workplaceId,
    onSuccess: () => {
      resetForm();
      onInvitationSuccess?.();
      modalState.closeModal();
    },
  });

  const handleClose = () => {
    sendWorkerInvitationDialogActionedEvent({
      workplaceId,
      openedFrom,
      action: "CANCEL",
      isAdmin,
    });
    resetForm();
    modalState.closeModal();
  };

  const rowGridTemplateColumns = isPrnWorkersInvitationRolloutEnabled
    ? {
        xs: "1fr",
        md: "minmax(0, 1fr) minmax(0, 1fr) 5.75rem",
      }
    : "minmax(0, 1fr) minmax(0, 1fr)";

  return (
    <ResponsiveDialog
      modalState={modalState}
      title="Invite Staff to Pick Up Shifts on Clipboard"
      width={700}
      footerContent={
        <DialogFooter
          secondaryAction={{
            label: "Cancel",
            variant: "outlined",
            onClick: handleClose,
          }}
          primaryAction={{
            label: "Send Invitations",
            variant: "primary",
            disabled: !hasAtLeastOneValidRow() || isSendingInvitations,
            isLoading: isSendingInvitations,
            onClick: () => {
              void handleSubmit();
            },
          }}
        />
      }
      onClose={handleClose}
    >
      <Stack spacing={3}>
        <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
          Add staff members you like working with to join Clipboard and pick up your shifts
        </Text>

        <Box
          sx={(theme) => ({
            display: { xs: "none", md: "grid" },
            gridTemplateColumns: rowGridTemplateColumns,
            gap: theme.spacing(3),
            alignItems: "center",
          })}
        >
          <Text bold variant="body2">
            Name
          </Text>
          <Text bold variant="body2">
            Phone Number
          </Text>
          {isPrnWorkersInvitationRolloutEnabled && (
            <Text bold variant="body2" sx={{ textAlign: "center" }}>
              PRN worker
            </Text>
          )}
        </Box>

        <Box
          ref={dialogContentRef}
          sx={(theme) => ({
            maxHeight: "18rem",
            overflowY: "auto",
            paddingRight: 1,
            paddingBottom: 1,
            marginRight: -1,
            scrollbarGutter: "stable",
            "& .MuiOutlinedInput-root": {
              backgroundColor: theme.palette.background.standout,
            },
          })}
        >
          {fields.map((field, index) => (
            <WorkerInvitationStaffMemberRow
              key={field.id}
              control={control}
              index={index}
              isPrnWorkersInvitationRolloutEnabled={isPrnWorkersInvitationRolloutEnabled}
              rowGridTemplateColumns={rowGridTemplateColumns}
              rowId={field.id}
              shouldShowFieldError={shouldShowFieldError}
            />
          ))}

          <Button
            variant="link"
            startIconType="plus"
            sx={(theme) => ({
              marginTop: theme.spacing(1),
              paddingLeft: 0,
              paddingRight: 0,
            })}
            onClick={addNewStaffMember}
          >
            Add more staff
          </Button>
        </Box>

        <Divider />
      </Stack>
    </ResponsiveDialog>
  );
}
